import { useEffect, useState, useCallback } from "react";
import DOMPurify from "dompurify";
import api, { fileUrl } from "@/lib/api";
import { StatusBadge, BlocSwitcher, seanceIconClass } from "@/components/shared";
import ProofModal from "@/components/ProofModal";
import ProofCard from "@/components/ProofCard";
import { Loader2, Target, ChevronRight, CheckCircle2, Plus, ShieldCheck, AlertTriangle, Download, FileText, ExternalLink, BookOpen, Eye, EyeOff, ListChecks, Lock } from "lucide-react";

// Defense-in-depth: force rel=noopener on links that open a new tab (prevents reverse-tabnabbing)
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// Returns the YouTube video id if the url is a YouTube link, else null
function youtubeId(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace("www.", "");
    if (host === "youtu.be") return u.pathname.slice(1) || null;
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1];
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1];
    }
  } catch (e) { return null; }
  return null;
}

export default function CoursPage() {
  const [sequences, setSequences] = useState([]);
  const [seqId, setSeqId] = useState(null);
  const [seq, setSeq] = useState(null);
  const [active, setActive] = useState(null);
  const [doneIds, setDoneIds] = useState(new Set());
  const [proofs, setProofs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [subSeanceForModal, setSubSeanceForModal] = useState(null);
  const [reviseIds, setReviseIds] = useState(new Set());
  const [previewLinks, setPreviewLinks] = useState(new Set());

  const loadProgress = useCallback(() => {
    api.get("/seance-progress").then(({ data }) => setDoneIds(new Set(data)));
  }, []);

  const loadNotifications = useCallback(() => {
    api.get("/notifications").then(({ data }) => setReviseIds(new Set((data.items || []).map((i) => i.seance_id)))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/sequences").then(({ data }) => {
      setSequences(data);
      setSeqId((p) => p || data[0]?.id);
    });
    loadProgress();
    loadNotifications();
  }, [loadProgress, loadNotifications]);

  useEffect(() => {
    if (!seqId) return;
    setSeq(null);
    setActive(null);
    api.get(`/sequences/${seqId}`).then(({ data }) => {
      setSeq(data);
      const cur = data.seances.find((s) => s.status === "en_cours") || data.seances[0];
      setActive(cur);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seqId]);

  const loadProofs = useCallback((seanceId) => {
    if (!seanceId) return setProofs([]);
    api.get(`/seances/${seanceId}/proofs`).then(({ data }) => setProofs(data));
  }, []);

  useEffect(() => {
    loadProofs(active?.id);
    setPreviewLinks(new Set());
  }, [active, loadProofs]);

  const togglePreviewLink = (id) => setPreviewLinks((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const afterChange = () => {
    loadProofs(active?.id);
    loadProgress();
    loadNotifications();
  };

  const closeModal = () => { setModalOpen(false); setSubSeanceForModal(null); };
  const openModal = (subId = null) => { setSubSeanceForModal(subId); setModalOpen(true); };

  const meta = sequences.find((s) => s.id === seqId);
  const hasSubSeances = active?.sub_seances?.length > 0;
  const doneSubSeanceIds = new Set(proofs.filter((p) => p.sub_seance_id).map((p) => p.sub_seance_id));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 mz-rise">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-orange-500 uppercase tracking-wide">{meta?.bloc} · {meta?.title}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1">Mes cours</h1>
          <p className="text-slate-500 font-medium mt-1">{meta?.subtitle}</p>
        </div>
        <BlocSwitcher sequences={sequences} value={seqId} onChange={setSeqId} />
      </div>

      {!seq ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 text-orange-400 animate-spin" /></div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6 mt-8">
          {/* list */}
          <div className="lg:col-span-2 space-y-2.5">
            {seq.seances.map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s)}
                data-testid={`seance-item-${s.order}`}
                className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${
                  active?.id === s.id ? "bg-white border-orange-300 shadow-md ring-2 ring-orange-100" : "bg-white border-slate-100 hover:border-slate-200 shadow-sm"
                }`}
              >
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-sm font-extrabold ${seanceIconClass(s.status)}`}>
                  {s.status === "vue" ? <CheckCircle2 className="h-5 w-5" /> : s.order}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-slate-400">Séance {s.order} · {s.competency_code}</p>
                  <p className="font-bold text-slate-800 truncate">{s.title}</p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <StatusBadge status={s.status} />
                  {s.locked ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-full" data-testid={`locked-badge-${s.order}`}>
                      <Lock className="h-3 w-3" /> Verrouillée
                    </span>
                  ) : reviseIds.has(s.id) ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`revise-badge-${s.order}`}>
                      <AlertTriangle className="h-3 w-3" /> À revoir
                    </span>
                  ) : doneIds.has(s.id) && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-green-600" data-testid={`done-badge-${s.order}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Validée
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* detail */}
          <div className="lg:col-span-3">
            {active && (
              <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-7 sm:p-9" data-testid="seance-detail">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-400">
                  <span className="bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-xs font-extrabold">{active.competency_code}</span>
                  <StatusBadge status={active.status} />
                  {active.locked && (
                    <span className="inline-flex items-center gap-1 text-xs font-extrabold text-red-600 bg-red-100 px-2.5 py-1 rounded-full" data-testid="seance-locked-badge">
                      <Lock className="h-3.5 w-3.5" /> Verrouillée
                    </span>
                  )}
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-3">{active.title}</h2>

                {active.locked && (
                  <div className="flex items-center gap-2 mt-4 bg-red-50 text-red-700 rounded-2xl px-4 py-3" data-testid="seance-locked-notice">
                    <Lock className="h-5 w-5 shrink-0" />
                    <p className="text-sm font-bold">Cette séance est verrouillée par ton professeur. Tu peux consulter le cours mais tu ne peux pas encore déposer de preuve.</p>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-4 bg-orange-50 text-orange-700 rounded-2xl px-4 py-3">
                  <Target className="h-5 w-5 shrink-0" />
                  <p className="text-sm font-bold">Niveau visé : {active.level_target}</p>
                </div>

                {active.course_links?.length > 0 && (
                  <div className="mt-5 space-y-3">
                    {active.course_links.map((cl) => (
                      <div key={cl.id}>
                        <div className="flex flex-wrap gap-2.5">
                          <a
                            href={cl.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`access-course-btn-${cl.id}`}
                            className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3.5 font-bold shadow-sm shadow-orange-200 transition-colors"
                          >
                            <BookOpen className="h-5 w-5" /> {cl.label} <ExternalLink className="h-4 w-4 opacity-80" />
                          </a>
                          <button
                            onClick={() => togglePreviewLink(cl.id)}
                            data-testid={`toggle-course-preview-${cl.id}`}
                            className="inline-flex items-center gap-2 rounded-2xl border-2 border-orange-200 text-orange-600 hover:bg-orange-50 px-6 py-3.5 font-bold transition-colors"
                          >
                            {previewLinks.has(cl.id) ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />} {previewLinks.has(cl.id) ? "Masquer l'aperçu" : "Aperçu"}
                          </button>
                        </div>
                        {previewLinks.has(cl.id) && (
                          <div className="mt-3 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50" data-testid={`course-preview-frame-${cl.id}`}>
                            <iframe src={cl.url} className="w-full h-[480px]" title={cl.label} referrerPolicy="no-referrer" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
                            <p className="text-xs text-slate-400 font-medium px-3 py-2 bg-white border-t border-slate-100">Si l'aperçu ne s'affiche pas (site protégé), utilise « {cl.label} ».</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {active.links?.length > 0 && (() => {
                  const embeds = active.links.map((l) => ({ ...l, yt: youtubeId(l.url) })).filter((l) => l.yt);
                  const buttons = active.links.filter((l) => !youtubeId(l.url));
                  return (
                    <>
                      {buttons.length > 0 && (
                        <div className="mt-5 space-y-2.5" data-testid="seance-links">
                          {buttons.map((l) => (
                            <div key={l.id}>
                              <div className="flex flex-wrap items-center gap-2.5">
                                <a href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`course-link-${l.id}`}
                                  className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 font-bold text-sm transition-colors">
                                  <ExternalLink className="h-4 w-4" /> {l.label}
                                </a>
                                <button onClick={() => togglePreviewLink(l.id)} data-testid={`toggle-preview-${l.id}`}
                                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600 px-4 py-2.5 font-bold text-sm transition-colors">
                                  {previewLinks.has(l.id) ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {previewLinks.has(l.id) ? "Masquer" : "Aperçu"}
                                </button>
                              </div>
                              {previewLinks.has(l.id) && (
                                <div className="mt-2 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50" data-testid={`preview-frame-${l.id}`}>
                                  <iframe src={l.url} className="w-full h-[400px]" title={l.label} referrerPolicy="no-referrer" sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {embeds.length > 0 && (
                        <div className="mt-5 space-y-4" data-testid="seance-embeds">
                          {embeds.map((l) => (
                            <div key={l.id}>
                              <p className="text-sm font-bold text-slate-700 mb-1.5 flex items-center gap-1.5"><ExternalLink className="h-4 w-4 text-orange-500" /> {l.label}</p>
                              <div className="relative w-full rounded-2xl overflow-hidden border border-slate-200 bg-black" style={{ paddingTop: "56.25%" }}>
                                <iframe
                                  className="absolute inset-0 w-full h-full"
                                  src={`https://www.youtube.com/embed/${l.yt}`}
                                  title={l.label}
                                  data-testid={`course-embed-${l.id}`}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="mt-6">
                  {active.content_html ? (
                    <div
                      className="prose prose-slate max-w-none mz-course-html"
                      data-testid="seance-html-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(active.content_html, { ADD_TAGS: ["style"], ADD_ATTR: ["target"] }),
                      }}
                    />
                  ) : (
                    <div className="prose prose-slate max-w-none">
                      {active.content.split("\n").map((line, i) =>
                        line.trim() === "" ? <div key={`sp-${i}`} className="h-2" /> : (
                          <p key={`ln-${i}`} className={`text-slate-600 font-medium leading-relaxed ${line.startsWith("-") ? "pl-4 -mb-1 flex gap-2" : "mb-2"}`}>
                            {line.startsWith("-") ? <><ChevronRight className="h-4 w-4 text-orange-400 mt-1 shrink-0" />{line.slice(1).trim()}</> : line}
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>

                {active.documents?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <p className="font-display text-lg font-semibold text-slate-900 mb-3 inline-flex items-center gap-2"><Download className="h-5 w-5 text-orange-500" /> Documents à télécharger</p>
                    <div className="grid sm:grid-cols-2 gap-2.5" data-testid="seance-documents">
                      {active.documents.map((d) => (
                        <a key={d.id} href={fileUrl(d.id)} target="_blank" rel="noopener noreferrer" download data-testid={`download-doc-${d.id}`}
                          className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-200 px-4 py-3 transition-colors group">
                          <div className="h-9 w-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 group-hover:border-orange-300">
                            <FileText className="h-4 w-4 text-slate-500 group-hover:text-orange-500" />
                          </div>
                          <span className="text-sm font-bold text-slate-700 truncate flex-1">{d.name}</span>
                          <Download className="h-4 w-4 text-slate-400 group-hover:text-orange-500 shrink-0" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* validation / preuves */}
                <div className="mt-7 pt-6 border-t border-slate-100">
                  {hasSubSeances && (
                    <div className="mb-6">
                      <p className="font-display text-lg font-semibold text-slate-900 mb-3 inline-flex items-center gap-2"><ListChecks className="h-5 w-5 text-orange-500" /> Étapes de cette séance</p>
                      <div className="space-y-2.5" data-testid="sub-seances-list">
                        {active.sub_seances.map((sub, idx) => {
                          const done = doneSubSeanceIds.has(sub.id);
                          return (
                            <div key={sub.id} data-testid={`sub-seance-item-${idx}`}
                              className={`flex items-center gap-3 rounded-2xl border p-4 ${done ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-100"}`}>
                              <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 font-extrabold text-sm ${done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                                {done ? <CheckCircle2 className="h-4 w-4" /> : idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-800 truncate">{sub.title}</p>
                                <p className="text-xs font-medium text-slate-400">{done ? "Preuve déposée" : "Pas encore déposée"}</p>
                              </div>
                              <button onClick={() => openModal(sub.id)} disabled={active.locked} data-testid={`sub-seance-deposit-${idx}`}
                                className={`inline-flex items-center gap-1.5 font-bold text-sm shrink-0 ${active.locked ? "text-slate-300 cursor-not-allowed" : "text-orange-600 hover:text-orange-700"}`}>
                                <Plus className="h-4 w-4" /> {done ? "Ajouter" : "Déposer"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {proofs.length === 0 ? (
                    hasSubSeances ? null : (
                      <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5 text-center">
                        <ShieldCheck className="h-8 w-8 text-orange-400 mx-auto" />
                        <p className="font-bold text-slate-700 mt-2">Valide cette séance</p>
                        <p className="text-sm text-slate-500 font-medium">Dépose tes preuves (vidéo, audio, fichier) + ta fiche d'autoévaluation.</p>
                        <button onClick={() => openModal()} disabled={active.locked} data-testid="open-proof-modal"
                          className={`mt-3 inline-flex items-center gap-2 text-white rounded-full px-6 py-2.5 font-bold text-sm ${active.locked ? "bg-slate-300 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"}`}>
                          <Plus className="h-4 w-4" /> Déposer mes preuves
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-500" /> Séance validée</p>
                        <button onClick={() => openModal()} disabled={active.locked} data-testid="add-proof-btn"
                          className={`inline-flex items-center gap-1.5 font-bold text-sm ${active.locked ? "text-slate-300 cursor-not-allowed" : "text-orange-600 hover:text-orange-700"}`}>
                          <Plus className="h-4 w-4" /> Ajouter une preuve
                        </button>
                      </div>
                      {proofs.map((p) => <ProofCard key={p.id} proof={p} onChanged={afterChange} />)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {active && (
        <ProofModal
          seance={active}
          open={modalOpen}
          onClose={closeModal}
          onSubmitted={afterChange}
          subSeances={active.sub_seances || []}
          initialSubSeanceId={subSeanceForModal}
        />
      )}
    </div>
  );
}
